// All editable copy, options and pricing for av-consultation.com/hiring live here.

export const SEO = {
    title: "Hire an Influencer Marketing Expert. Anywhere in the World. | AV Consultation",
    description:
        "Access a global network of highly vetted influencer marketing professionals across 50+ cities. Full-time or part-time. Remote, in-person or hybrid. Shortlist in 5–7 days.",
    url: "https://av-consultation.com/hiring",
    image: "https://av-consultation.com/og-hiring.jpg",
};

export const NAV_LINKS = [
    { label: "How It Works", id: "how-it-works" },
    { label: "Why Us", id: "why-us" },
    { label: "Pricing", id: "pricing" },
];

export const HERO = {
    headline: "Hire an Influencer Marketing Expert. Anywhere in the World.",
    sub1: "Access a global network of highly vetted influencer marketing professionals across 50+ cities.",
    sub2: "Whether you're starting your influencer marketing channel, scaling an existing program or building an in-house team, we'll help you find the right talent.",
    sub3: "Full-time or part-time. Remote, in-person or hybrid. From the city or country of your choice.",
    primaryCta: "Find Your Talent",
    secondaryCta: "Explore How It Works",
};

export const FORM = {
    heading: "Tell Us Who You Want to Hire",
    description:
        "Share your requirements. We'll find and shortlist 3–4 professionals who match your expectations.",
    submit: "Find My Influencer Marketing Hire",
    successTitle: "Thank you!",
    successMessage:
        "Thank you! We've received your hiring requirements. Our team will reach out shortly to understand your needs.",
    roles: [
        "Influencer Marketing Specialist",
        "Head of Partnerships",
        "Talent Manager",
        "TikTok Shop Expert",
        "Affiliate Manager",
        "Community Manager",
    ],
    employmentTypes: ["Part-time", "Full-time"],
    workArrangements: ["Remote", "In-person", "Hybrid"],
    locationPreferences: [
        { value: "city", label: "Based in this city" },
        { value: "country", label: "Based in this country" },
        { value: "anywhere", label: "Anywhere in the world" },
    ],
    experienceLevels: [
        "Mid-level professional",
        "Senior professional (5+ years)",
        "Open to recommendations",
    ],
    engagementDurations: ["Short-term project", "Long-term position"],
    currencies: ["USD", "EUR", "GBP", "INR", "AUD", "CAD", "SGD", "AED", "PHP"],
    packages: ["Starter", "Growth", "Scale", "Hire + Assist"],
};

export const HOW_IT_WORKS = {
    heading: "Hiring Shouldn't Take 30–45 Days.",
    sub: "We've simplified the entire recruitment process so you can focus on selecting the right person instead of screening endless applications.",
    steps: [
        {
            title: "Tell Us What You Need",
            body: "Fill out our hiring form. We'll connect with you to understand the role, experience requirements, budget, location preferences and company culture.",
        },
        {
            title: "We Find the Right Professionals",
            body: "We source candidates from our global network and evaluate them against your specific requirements.",
        },
        {
            title: "Meet Your Shortlisted Candidates",
            body: "Within 5–7 days, we aim to present 3–4 qualified candidates for your final interviews. If the initial shortlist isn't the right fit, we'll keep presenting more options until you find the right person.",
        },
        {
            title: "Make Your Choice",
            body: "Once you've selected your preferred candidate, we'll assist with the onboarding process to ensure a smooth start.",
        },
        {
            title: "We've Got Your Back",
            body: "Don't like the candidates? We refund the whole amount. And if your hire doesn't work out within your selected replacement period, we'll find a replacement at no additional recruitment fee.",
        },
    ],
    stats: [
        { value: "3–4", label: "shortlisted candidates" },
        { value: "5–7 days", label: "to shortlist" },
        { value: "10–12 days", label: "target hiring timeline" },
    ],
    statsNote:
        "The actual hiring timeline depends on your availability for final interviews and your decision-making process.",
};

export const SHORTLIST_PREVIEW = {
    eyebrow: "What you receive",
    heading: "This Is What a Shortlist Looks Like.",
    sub: "Every candidate arrives with a verified summary, so your final interviews start from an informed position. Details below are anonymized examples, not real candidates.",
    cards: [
        {
            ref: "AVC-0417",
            role: "Influencer Marketing Specialist",
            level: "Senior · 6 yrs",
            location: "Remote · Philippines",
            employment: "Full-time",
            summary: "Ran creator programs for two DTC skincare brands, managing 120+ monthly creator partnerships across TikTok and Instagram.",
            skills: ["Creator sourcing", "TikTok Shop", "Negotiation", "Reporting"],
            checks: ["Identity verified", "Employment verified", "2 references", "Skills assessed"],
            fit: 94,
        },
        {
            ref: "AVC-0522",
            role: "Head of Partnerships",
            level: "Senior · 8 yrs",
            location: "In-person · New York",
            employment: "Full-time",
            summary: "Built an agency partnerships function from scratch, closing long-term ambassador deals and managing a six-figure creator budget.",
            skills: ["Ambassador programs", "Contracting", "Team leadership", "Budgeting"],
            checks: ["Identity verified", "Employment verified", "3 references", "Skills assessed"],
            fit: 91,
        },
        {
            ref: "AVC-0609",
            role: "Affiliate Manager",
            level: "Mid-level · 3 yrs",
            location: "Hybrid · London",
            employment: "Part-time",
            summary: "Scaled an affiliate and creator-code program for a consumer app, doubling tracked creator revenue in nine months.",
            skills: ["Affiliate platforms", "Creator codes", "Attribution", "Outreach"],
            checks: ["Identity verified", "Employment verified", "2 references", "Skills assessed"],
            fit: 88,
        },
    ],
    note: "Anonymized, illustrative profiles. Real shortlists are built against your role and shared with candidate consent.",
};

export const GUARANTEE = {
    label: "100% Money-Back Guarantee",
    short: "Don't like the candidates? Full refund.",
    heading: "Don't like the candidates? We refund the whole amount.",
    body: "If you're not happy with the candidates we present, you get a full refund of your package fee. No partial credits, no awkward conversations. We only win when you hire someone you're genuinely excited about.",
    points: ["Full refund of your package fee", "No questions asked", "Replacement guarantee after you hire"],
};

export const WHY_US = {
    heading: "We're Not a Job Board. We're a Specialist Talent Network.",
    sub: "Conventional hiring puts the entire screening burden on you. We take it off your plate.",
    jobSites: {
        title: "Hiring Through Job Sites",
        items: [
            "Dozens or hundreds of applications.",
            "Manually screen every candidate.",
            "Verify previous employment and experience.",
            "Conduct multiple rounds of interviews.",
            "Evaluate technical skills yourself.",
            "Assess cultural fit.",
            "Repeat the process if the candidate doesn't work out.",
        ],
    },
    avc: {
        title: "Hiring Through AV Consultation",
        items: [
            "Access to a specialist influencer marketing talent network.",
            "Pre-screened and evaluated candidates.",
            "Verified professional background.",
            "Reference checks with previous employers, with candidate consent.",
            "Practical skills evaluation.",
            "Candidates matched against your role and company requirements.",
            "Replacement support if the hire doesn't work out within the selected guarantee period.",
        ],
    },
    closing:
        "You don't need hundreds of CVs. You need 3\u2060–\u20604 professionals who are genuinely suited to the role.",
};

export const VERIFICATION = {
    heading: "We Don't Send You Unverified CVs.",
    sub: "Every shortlisted professional goes through a structured evaluation process before being presented to you.",
    cards: [
        {
            title: "Identity & Background Verification",
            body: "Relevant identity and professional background details are verified.",
            status: "Verified",
        },
        {
            title: "Employment Verification",
            body: "Previous employment and relevant professional experience are cross-checked.",
            status: "Cross-checked",
        },
        {
            title: "Reference Checks",
            body: "We seek feedback from previous employers or professional references, with appropriate candidate consent.",
            status: "Completed",
        },
        {
            title: "Practical Skills Assessment",
            body: "We evaluate relevant campaign experience, practical skills and role-specific capabilities.",
            status: "Assessed",
        },
        {
            title: "Role & Culture Fit",
            body: "We assess experience, seniority, availability, location, budget expectations and working preferences against your requirements.",
            status: "Matched",
        },
    ],
    closing:
        "We don't just match CVs. We evaluate professionals against the actual requirements of your role.",
};

export const NETWORK = {
    heading: "A Global Network. Talent That Fits Your Requirements.",
    questions: [
        "Need someone in New York to work from your office?",
        "Looking for a remote influencer marketing specialist based in the Philippines or India?",
        "Need someone for a three-month project or a long-term position?",
    ],
    body1: "Our network spans 50+ cities, giving you access to professionals across different countries, experience levels and specializations.",
    body2: "Whether you're a large agency looking for a senior professional with 5+ years of experience or a growing brand looking for a capable mid-level specialist within budget, we'll work around your requirements.",
    highlights: [
        "50+ cities",
        "Global remote hiring",
        "Local in-person hiring",
        "Full-time and part-time options",
        "Mid-level and senior professionals",
    ],
    mapNote: "Illustrative locations. Availability varies by city and role.",
};

// Illustrative marker locations only
export const MAP_PINS = [
    { city: "New York", lat: 40.71, lng: -74.01 },
    { city: "Los Angeles", lat: 34.05, lng: -118.24 },
    { city: "Toronto", lat: 43.65, lng: -79.38 },
    { city: "Mexico City", lat: 19.43, lng: -99.13 },
    { city: "São Paulo", lat: -23.55, lng: -46.63 },
    { city: "Buenos Aires", lat: -34.6, lng: -58.38 },
    { city: "London", lat: 51.51, lng: -0.13 },
    { city: "Lisbon", lat: 38.72, lng: -9.14 },
    { city: "Berlin", lat: 52.52, lng: 13.4 },
    { city: "Warsaw", lat: 52.23, lng: 21.01 },
    { city: "Istanbul", lat: 41.01, lng: 28.98 },
    { city: "Lagos", lat: 6.52, lng: 3.38 },
    { city: "Nairobi", lat: -1.29, lng: 36.82 },
    { city: "Cape Town", lat: -33.92, lng: 18.42 },
    { city: "Dubai", lat: 25.2, lng: 55.27 },
    { city: "Mumbai", lat: 19.08, lng: 72.88 },
    { city: "Bengaluru", lat: 12.97, lng: 77.59 },
    { city: "Singapore", lat: 1.35, lng: 103.82 },
    { city: "Manila", lat: 14.6, lng: 120.98 },
    { city: "Jakarta", lat: -6.21, lng: 106.85 },
    { city: "Ho Chi Minh City", lat: 10.82, lng: 106.63 },
    { city: "Seoul", lat: 37.57, lng: 126.98 },
    { city: "Tokyo", lat: 35.68, lng: 139.69 },
    { city: "Sydney", lat: -33.87, lng: 151.21 },
];

export const PRICING = {
    heading: "Simple, Transparent Hiring Packages.",
    sub: "Choose the level of support you need. Every package starts with the same hiring form.",
    flagship: {
        name: "Hire + Assist",
        badge: "Flagship",
        price: "$2,495",
        priceNote: "one-time",
        headline:
            "Don't Just Hire an Influencer Marketing Professional. Build the Entire System They Need to Succeed.",
        description:
            "Ideal for brands that want to start their influencer marketing channel or improve the performance of an existing program. We find your hire, build the operating system and work alongside them for their first six weeks.",
        sections: [
            {
                title: "Influencer Marketing Strategy & Infrastructure",
                items: [
                    "Ideal Customer Profile (ICP) research.",
                    "Creator niche and hashtag research.",
                    "Creator discovery framework.",
                    "Outreach strategy and messaging templates.",
                    "Follow-up sequences.",
                    "Content briefs.",
                ],
            },
            {
                title: "Complete SOPs & Documentation",
                items: [
                    "Documented influencer marketing workflows.",
                    "Creator discovery and evaluation processes.",
                    "Outreach and follow-up procedures.",
                    "Campaign management workflows.",
                    "Performance tracking and reporting framework.",
                ],
            },
            {
                title: "Six Weeks of Management & Training",
                items: [
                    "Work alongside your new hire for six weeks.",
                    "Train them on the systems and processes developed.",
                    "Supervise campaign execution.",
                    "Review their work and provide feedback.",
                    "Optimize workflows and resolve execution gaps.",
                    "Prepare them to independently operate the influencer program.",
                ],
            },
        ],
        highlight: "We Build the Engine. Your Hire Keeps It Running.",
        cta: "Get Started",
    },
    hireOnly: {
        name: "Hire Only",
        description:
            "We find, verify and shortlist the right influencer marketing professional. You conduct the final interviews and select your preferred candidate. We assist with onboarding.",
        tiers: [
            {
                name: "Starter",
                price: "$495",
                hires: "1 hire",
                guarantee: "45-Day Replacement Guarantee",
                items: [
                    "One influencer marketing hire.",
                    "Candidate background and experience verification.",
                    "Reference checks.",
                    "Role and culture-fit assessment.",
                    "Shortlist of 3–4 candidates.",
                    "Onboarding assistance.",
                    "45-day replacement if the hire doesn't work out.",
                ],
                cta: "Get Started",
            },
            {
                name: "Growth",
                price: "$795",
                hires: "2 hires in 6 months",
                guarantee: "90-Day Replacement Guarantee",
                intro: "Everything included in Starter, plus:",
                items: [
                    "Two influencer marketing hires within 6 months.",
                    "Extended 90-day replacement on each hire.",
                ],
                cta: "Get Started",
            },
            {
                name: "Scale",
                price: "$995",
                hires: "3 hires in 12 months",
                guarantee: "180-Day Replacement Guarantee",
                intro: "Everything included in Growth, plus:",
                items: [
                    "Three influencer marketing hires within a year.",
                    "Extended 180-day replacement on each hire.",
                ],
                cta: "Get Started",
            },
        ],
        note: "All Hire Only packages support full-time, part-time, remote, in-person and hybrid hiring.",
    },
    terms:
        "Replacement windows start from the hire's date of joining. Replacement guarantees are subject to the agreed role and hiring terms. Money-back guarantee applies to the package fee if you don't proceed with any presented candidate.",
};

export const FINAL_CTA = {
    heading: "Ready to Find Your Next Influencer Marketing Hire?",
    body: "Tell us what you're looking for. We'll take care of finding, evaluating and shortlisting the right professionals.",
    cta: "Find Your Talent",
};

export const FOOTER = {
    brand: "AV Consultation",
    tagline: "Specialist influencer marketing recruitment and team building.",
    nav: [
        { label: "How It Works", id: "how-it-works" },
        { label: "Why Us", id: "why-us" },
        { label: "Global Network", id: "global-network" },
        { label: "Pricing", id: "pricing" },
        { label: "Hire Talent", id: "hire-form" },
    ],
    contact: [
        { label: "ambuj@av-consultation.com", href: "mailto:ambuj@av-consultation.com", key: "email" },
        { label: "LinkedIn", href: "https://www.linkedin.com/in/ambuj-vats/", key: "linkedin" },
    ],
    legal: [
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Terms & Conditions", href: "/terms" },
    ],
};
